'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import GlobalSearch from '@/components/GlobalSearch';
import { Search, Plus, Bell, CheckSquare, MessageSquare, HelpCircle, ChevronDown, User, Settings, LogOut, Moon, Building2, Layers, Receipt, DollarSign, ClipboardList, Menu, X, Syringe } from 'lucide-react';
import { useRole } from '@/lib/useRole';
import { ROLE_LABELS, ROLE_COLORS } from '@/lib/rbac';

// ─── Quick Create items (scoped to user request: Daily Record, Flock, Sale, Expense, Vaccination) ───
const QUICK_CREATE_ITEMS = [
  {
    id: 'qc-daily',
    label: 'Daily Record',
    icon: ClipboardList,
    href: '/daily-records',
    shortcut: 'D',
    description: 'Log today\'s farm data',
  },
  {
    id: 'qc-flock',
    label: 'New Flock',
    icon: Layers,
    href: '/flock-management',
    shortcut: 'F',
    description: 'Register a new flock',
  },
  {
    id: 'qc-sale',
    label: 'Sale',
    icon: Receipt,
    href: '/sales',
    shortcut: 'S',
    description: 'Record a sale transaction',
  },
  {
    id: 'qc-expense',
    label: 'Expense',
    icon: DollarSign,
    href: '/expenses',
    shortcut: 'E',
    description: 'Log a farm expense',
  },
  {
    id: 'qc-vaccination',
    label: 'Vaccination',
    icon: Syringe,
    href: '/health',
    shortcut: 'V',
    description: 'Schedule or record vaccination',
  },
  {
    id: 'qc-task',
    label: 'Task',
    icon: CheckSquare,
    href: '/tasks',
    shortcut: 'T',
    description: 'Create a new task',
  },
];

const FARMS = [
  { id: 'farm-all', label: 'All Farms', branch: 'All branches' },
  { id: 'farm-y77', label: 'Y77 Poultry Farm', branch: 'Main Branch' },
  { id: 'farm-idudi', label: 'Idudi Poultry Farm', branch: 'Main Branch' },
  { id: 'farm-kampala', label: 'Kampala Demo Farm', branch: 'Kampala Branch' },
];

const NOTIFICATIONS = [
  { id: 'notif-001', type: 'warning', message: 'Vaccination due: Newcastle — Flock LY-2026-003', time: '10 min ago' },
  { id: 'notif-002', type: 'danger', message: 'High mortality alert: Flock BR-2026-002 — 18 deaths today', time: '34 min ago' },
  { id: 'notif-003', type: 'warning', message: 'Low stock: Broiler Starter — 3 days remaining', time: '1 hr ago' },
  { id: 'notif-004', type: 'info', message: 'Purchase Order PO-2026-041 approved by Okello James', time: '2 hrs ago' },
  { id: 'notif-005', type: 'success', message: 'Flock BR-2026-001 weight target achieved — 2.4 kg avg', time: '3 hrs ago' },
];

interface TopbarProps {
  onMobileMenuToggle: () => void;
  mobileMenuOpen: boolean;
}

export default function Topbar({ onMobileMenuToggle, mobileMenuOpen }: TopbarProps) {
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [farmOpen, setFarmOpen] = useState(false);
  const [selectedFarm, setSelectedFarm] = useState(FARMS[0]);
  const [searchOpen, setSearchOpen] = useState(false);

  const quickRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const farmRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const { user, role, can } = useRole();
  const roleLabel = ROLE_LABELS[role];
  const roleColors = ROLE_COLORS[role];

  // Default farm selector to user's assigned farm once session loads
  useEffect(() => {
    if (user.farmId) {
      const match = FARMS.find((f) => f.id === user.farmId);
      if (match) setSelectedFarm(match);
    }
  }, [user.farmId]);

  // Filter quick-create items by role permissions
  const visibleQuickCreate = QUICK_CREATE_ITEMS.filter(item => {
    if (item.id === 'qc-sale') return can('create_invoices');
    if (item.id === 'qc-expense') return can('create_expenses');
    return true; // daily, flock, vaccination visible to all
  });

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (quickRef.current && !quickRef.current.contains(e.target as Node)) setQuickCreateOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileOpen(false);
      if (farmRef.current && !farmRef.current.contains(e.target as Node)) setFarmOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Ctrl+K / Cmd+K global shortcut
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        setQuickCreateOpen(false);
      }
      // Quick-create shortcuts when dropdown is open
      if (quickCreateOpen) {
        const item = QUICK_CREATE_ITEMS.find((i) => i.shortcut.toLowerCase() === e.key.toLowerCase());
        if (item) {
          e.preventDefault();
          router.push(item.href);
          setQuickCreateOpen(false);
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [quickCreateOpen, router]);

  const notifColors: Record<string, string> = {
    warning: 'var(--warning)',
    danger: 'var(--danger)',
    info: 'var(--info)',
    success: 'var(--success)',
  };

  function handleLogout() {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('auth_user');
    }
    router.push('/auth/login');
  }

  return (
    <>
      {/* Global Search Modal */}
      <GlobalSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        selectedFarm={selectedFarm.label}
      />

      <header
        className="flex items-center gap-2 px-4 border-b bg-card"
        style={{ height: '64px', borderColor: 'var(--border)', zIndex: 40, position: 'relative' }}
      >
        {/* Mobile menu button */}
        <button
          onClick={onMobileMenuToggle}
          className="btn-ghost lg:hidden p-2"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>

        {/* Mobile logo */}
        <div className="flex items-center gap-2 lg:hidden">
          <AppLogo size={28} />
          <span className="font-bold text-sm text-foreground">Y77 Poultry</span>
        </div>

        {/* Search bar — opens GlobalSearch on click */}
        <div className="hidden md:flex flex-1 max-w-md">
          <button
            onClick={() => setSearchOpen(true)}
            className="relative w-full flex items-center text-left"
            aria-label="Open search (Ctrl+K)"
          >
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <div
              className="input-field pl-9 py-2 text-sm w-full cursor-pointer hover:border-primary transition-colors"
              style={{ height: '36px', color: 'var(--muted-foreground)' }}
            >
              Search flocks, customers, invoices…
            </div>
            <kbd
              className="absolute right-2 top-1/2 -translate-y-1/2 px-1.5 py-0.5 text-xs rounded border text-muted-foreground pointer-events-none"
              style={{ borderColor: 'var(--border)', fontSize: '0.65rem' }}
            >
              ⌘K
            </kbd>
          </button>
        </div>

        <div className="flex items-center gap-1 ml-auto">
          {/* Mobile search icon */}
          <button
            onClick={() => setSearchOpen(true)}
            className="btn-ghost p-2 md:hidden"
            aria-label="Search"
          >
            <Search size={18} />
          </button>

          {/* Farm selector */}
          <div ref={farmRef} className="relative hidden md:block">
            <button
              onClick={() => setFarmOpen(!farmOpen)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm font-medium transition-colors"
              style={{ borderColor: 'var(--border)', color: 'var(--foreground)', fontSize: '0.8rem' }}
            >
              <Building2 size={14} style={{ color: 'var(--primary)' }} />
              <span className="max-w-[120px] truncate">{selectedFarm.label}</span>
              <ChevronDown size={12} className="text-muted-foreground" />
            </button>
            {farmOpen && (
              <div
                className="absolute right-0 top-full mt-1 w-56 bg-card rounded-lg border shadow-modal py-1 z-50 fade-in"
                style={{ borderColor: 'var(--border)' }}
              >
                {FARMS.map((farm) => (
                  <button
                    key={farm.id}
                    onClick={() => { setSelectedFarm(farm); setFarmOpen(false); }}
                    className="w-full flex flex-col items-start px-3 py-2 text-left hover:bg-muted transition-colors"
                  >
                    <span className="text-sm font-medium text-foreground">{farm.label}</span>
                    <span className="text-xs text-muted-foreground">{farm.branch}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Quick create */}
          <div ref={quickRef} className="relative">
            <button
              onClick={() => setQuickCreateOpen(!quickCreateOpen)}
              className="btn-primary py-1.5 px-3 text-sm"
              style={{ height: '36px' }}
            >
              <Plus size={15} />
              <span className="hidden sm:inline">New</span>
            </button>
            {quickCreateOpen && (
              <div
                className="absolute right-0 top-full mt-1 w-64 bg-card rounded-xl border shadow-modal py-2 z-50 fade-in"
                style={{ borderColor: 'var(--border)' }}
              >
                <div className="px-3 pb-1.5 flex items-center justify-between">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide" style={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.08em' }}>
                    Quick Create
                  </span>
                  <span className="text-xs text-muted-foreground" style={{ fontSize: '0.65rem' }}>Press shortcut key</span>
                </div>
                <div className="px-2">
                  {visibleQuickCreate.map((item) => {
                    const ItemIcon = item.icon;
                    return (
                      <Link
                        key={item.id}
                        href={item.href}
                        onClick={() => setQuickCreateOpen(false)}
                        className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm text-foreground hover:bg-muted transition-colors group"
                      >
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: 'var(--primary-light, rgba(34,197,94,0.12))' }}
                        >
                          <ItemIcon size={15} style={{ color: 'var(--primary)' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm leading-tight">{item.label}</div>
                          <div className="text-xs text-muted-foreground leading-tight mt-0.5 truncate">{item.description}</div>
                        </div>
                        <kbd
                          className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded border text-xs font-bold text-muted-foreground group-hover:border-primary group-hover:text-primary transition-colors"
                          style={{ borderColor: 'var(--border)', fontSize: '0.7rem' }}
                        >
                          {item.shortcut}
                        </kbd>
                      </Link>
                    );
                  })}
                </div>
                <div className="mx-3 mt-2 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
                  <p className="text-xs text-muted-foreground text-center" style={{ fontSize: '0.65rem' }}>
                    Use <kbd className="px-1 py-0.5 rounded border" style={{ borderColor: 'var(--border)' }}>⌘K</kbd> to search everything
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Notifications */}
          <div ref={notifRef} className="relative">
            <button
              onClick={() => setNotifOpen(!notifOpen)}
              className="btn-ghost relative p-2"
              aria-label="Notifications"
            >
              <Bell size={18} />
              <span
                className="absolute top-1 right-1 w-4 h-4 rounded-full text-white flex items-center justify-center"
                style={{ backgroundColor: 'var(--danger)', fontSize: '0.6rem', fontWeight: 700 }}
              >
                7
              </span>
            </button>
            {notifOpen && (
              <div
                className="absolute right-0 top-full mt-1 w-80 bg-card rounded-lg border shadow-modal z-50 fade-in overflow-hidden"
                style={{ borderColor: 'var(--border)' }}
              >
                <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
                  <span className="text-sm font-600 text-foreground" style={{ fontWeight: 600 }}>Notifications</span>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: 'var(--danger)', fontSize: '0.65rem' }}>7 new</span>
                </div>
                <div className="max-h-72 overflow-y-auto scrollbar-thin">
                  {NOTIFICATIONS.map((n) => (
                    <div key={n.id} className="flex gap-3 px-4 py-3 border-b hover:bg-muted transition-colors cursor-pointer" style={{ borderColor: 'var(--border)' }}>
                      <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: notifColors[n.type] }} />
                      <div>
                        <p className="text-xs text-foreground leading-relaxed">{n.message}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{n.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-4 py-2 text-center">
                  <Link href="/notifications" className="text-xs font-medium" style={{ color: 'var(--primary)' }} onClick={() => setNotifOpen(false)}>
                    View all notifications
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Tasks */}
          <Link href="/tasks" className="btn-ghost relative p-2 hidden sm:flex" aria-label="Tasks">
            <CheckSquare size={18} />
            <span
              className="absolute top-1 right-1 w-4 h-4 rounded-full text-white flex items-center justify-center"
              style={{ backgroundColor: 'var(--warning)', fontSize: '0.6rem', fontWeight: 700 }}
            >
              5
            </span>
          </Link>

          {/* Messages */}
          <button className="btn-ghost p-2 hidden sm:flex" aria-label="Messages">
            <MessageSquare size={18} />
          </button>

          {/* Help */}
          <button className="btn-ghost p-2 hidden md:flex" aria-label="Help">
            <HelpCircle size={18} />
          </button>

          {/* User profile */}
          <div ref={profileRef} className="relative">
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className="flex items-center gap-2 pl-2 pr-1 py-1 rounded-lg hover:bg-muted transition-colors"
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                style={{ backgroundColor: 'var(--primary)', fontSize: '0.75rem' }}
              >
                {user.initials}
              </div>
              <div className="hidden md:block text-left">
                <div className="text-xs font-600 text-foreground leading-tight" style={{ fontWeight: 600 }}>{user.name}</div>
                <div className="flex items-center gap-1">
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold leading-tight ${roleColors.bg} ${roleColors.text}`} style={{ fontSize: '0.6rem' }}>
                    {roleLabel}
                  </span>
                </div>
              </div>
              <ChevronDown size={12} className="text-muted-foreground hidden md:block" />
            </button>
            {profileOpen && (
              <div
                className="absolute right-0 top-full mt-1 w-52 bg-card rounded-lg border shadow-modal py-1 z-50 fade-in"
                style={{ borderColor: 'var(--border)' }}
              >
                <div className="px-3 py-2 border-b" style={{ borderColor: 'var(--border)' }}>
                  <div className="text-sm font-600 text-foreground" style={{ fontWeight: 600 }}>{user.name}</div>
                  <div className="text-xs text-muted-foreground">{user.email}</div>
                  <span className={`inline-flex mt-1 text-xs px-2 py-0.5 rounded-full font-semibold ${roleColors.bg} ${roleColors.text}`} style={{ fontSize: '0.65rem' }}>
                    {roleLabel}
                  </span>
                </div>
                {[
                  { id: 'prof-profile', icon: User, label: 'User Profile', href: '/profile' },
                  { id: 'prof-account', icon: Settings, label: 'My Account', href: '/account' },
                  { id: 'prof-settings', icon: Settings, label: 'Settings', href: '/settings' },
                  { id: 'prof-dark', icon: Moon, label: 'Dark Theme', href: '#' },
                ].map((item) => {
                  const ItemIcon = item.icon;
                  return (
                    <Link
                      key={item.id}
                      href={item.href}
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                    >
                      <ItemIcon size={14} className="text-muted-foreground" />
                      {item.label}
                    </Link>
                  );
                })}
                <div className="border-t mt-1 pt-1" style={{ borderColor: 'var(--border)' }}>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2.5 px-3 py-2 text-sm w-full text-left hover:bg-muted transition-colors"
                    style={{ color: 'var(--danger)' }}
                  >
                    <LogOut size={14} />
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
}